import React, { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Accordion } from '../accordion/Accordion';
import officeApi from '../../../api/officeApi';
import * as CalendarActions from '../../actions/calendar';
import Svg from '../shared/Svg';
import { recurrenceMap } from '../../utils/calendar';
import { useInterval, useMountedState } from '../../utils/hooks';
import './CalendarEvent.scss';
import { PopOutIcon } from '../shared/PopOutIcon';
import { Tooltip } from '../shared/Tooltip';

export const CalendarEvent = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const dispatch = useDispatch();
  const isMounted = useMountedState();
  const isCalendarEventSaved = useSelector((state) => state.calendar.isCalendarEventSaved);
  const eventId = useSelector((state) => state.calendar.eventId);
  const isComposingEvent = useSelector((state) => state.calendar.isComposingEvent);
  const start = useSelector((state) => state.calendar.eventStart);
  const end = useSelector((state) => state.calendar.eventEnd);
  const location = useSelector((state) => state.calendar.eventLocation);
  const subject = useSelector((state) => state.calendar.eventSubject);
  const recurrence = useSelector((state) => state.calendar.eventRecurrence);
  const repeats = !recurrence ? 'This event does not repeat' : recurrenceMap(recurrence);
  const user = useSelector((state) => state.auth.user);
  const salesforceEvent = useSelector((state) => state.calendar.salesforceEventId);
  const redirectLink = `${user.salesforceInstanceUrl}/lightning/r/Event/${salesforceEvent}/view`;

  const dateTimeOptions = {
    dateStyle: 'short',
    timeStyle: 'short',
  };

  const getAllDay = (start, end) => {
    const timeDifference = end.getTime() - start.getTime();
    const fullDay = timeDifference % (24 * 60 * 60 * 1000) === 0;
    const singleDay = fullDay && (Math.abs(start.getTime() - end.getTime()) === 24 * 60 * 60 * 1000);
    let allDayEnd;
    if (fullDay && !isComposingEvent) {
      const tempEnd = end;
      allDayEnd = new Date(singleDay ? tempEnd.setDate(end.getDate() - 1) : tempEnd.setDate(end.getDate())).toLocaleString('en-US', dateTimeOptions).split(',')[0].trim();
    } else {
      allDayEnd = end.toLocaleString('en-US', dateTimeOptions).split(',')[0].trim();
    }
    return { fullDay, singleDay, allDayEnd };
  };

  const buildDateString = (startDate, endDate, singleDay, fullDay, allDayEnd) => {
    let dateString = startDate;
    if (!singleDay && (startDate !== endDate)) {
      const multiDayEnd = fullDay ? allDayEnd : endDate;
      dateString = dateString + ' - ' + multiDayEnd;
    }
    if (fullDay) {
      dateString += ' (All Day)';
    }
    return dateString;
  };

  const [startDate, startTime] = start.toLocaleString('en-US', dateTimeOptions).split(',').map((str) => str.trim());
  const [endDate, endTime] = end.toLocaleString('en-US', dateTimeOptions).split(',').map((str) => str.trim());
  const { fullDay, singleDay, allDayEnd } = getAllDay(start, end);

  useInterval(() => {
    eventChangedHandler();
  }, isComposingEvent ? 1000 : null);

  const eventChangedHandler = async () => {
    if (isComposingEvent) {
      if (isMounted()) {
        const [newSubject, newStart, newEnd, newLocation, newRecurrence] = await Promise.all([
          officeApi.getEventSubject(),
          officeApi.getEventStart(),
          officeApi.getEventEnd(),
          officeApi.getEventLocation(),
          officeApi.getEventRecurrence(),
        ]);

        const allDay = getAllDay(newStart, newEnd).fullDay;
        batch(() => {
          if (allDay !== fullDay) dispatch(CalendarActions.setEventAllDay(allDay));
          if (newSubject !== subject) {
            dispatch(CalendarActions.setEventSubject(newSubject));
            isCalendarEventSaved && !eventId && dispatch(CalendarActions.attendeeChange(true));
          }
          if (newStart.getTime() !== start.getTime()) {
            dispatch(CalendarActions.setEventStart(newStart));
            isCalendarEventSaved && !eventId && dispatch(CalendarActions.attendeeChange(true));
          }
          if (newEnd.getTime() !== end.getTime()) dispatch(CalendarActions.setEventEnd(newEnd));
          if (newLocation !== location) dispatch(CalendarActions.setEventLocation(newLocation));
          if (JSON.stringify(newRecurrence) !== JSON.stringify(recurrence)) dispatch(CalendarActions.setEventRecurrence(newRecurrence));
        });
      }
    }
  };

  return (
    <div className="calendarEvent">
      <Accordion title="Litify Calendar" status={isExpanded} setStatus={setIsExpanded} />
      { isExpanded &&
        <div className="calendarEvent-extended">
          <Svg className="calendarEvent-image" data='/assets/event.svg'/>
          <div className="calendarEvent-details">
            <Tooltip textContent={subject}>
              <p className='calendarEvent-title'><strong>{subject}</strong></p>
            </Tooltip>
            <Tooltip textContent={'Date: ' + buildDateString(startDate, endDate, singleDay, fullDay, allDayEnd)}>
              <p className='calendarEvent-details'>
                <strong>Date: </strong>{buildDateString(startDate, endDate, singleDay, fullDay, allDayEnd)}
              </p>
            </Tooltip>
            {!fullDay &&
              <Tooltip textContent={`Time: ${startTime} - ${endTime}`}>
                <p className='calendarEvent-details'>
                  <strong>Time: </strong>{`${startTime} - ${endTime}`}
                </p>
              </Tooltip>
            }
            <Tooltip textContent={`Location: ${location}`}>
              <p className="calendarEvent-details"><strong>Location:</strong> {location}</p>
            </Tooltip>
            <Tooltip textContent={`Repeats: ${repeats}`}>
              <p className="calendarEvent-details"><strong>Repeats:</strong> {repeats}</p>
            </Tooltip>
          </div>
          { salesforceEvent && <PopOutIcon
            link={redirectLink}
            className='attendee-redirect'
          /> }
        </div>
      }
    </div>
  );
};
